import { CookieService, DeviceDetectorService } from 'ngx-unificator/services';
import { delay, distinctUntilChanged, filter, map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { PlatformService } from '../../core/services/platform.service';
import { UserService } from '../../core/services/user/user.service';
import { BonusesStatuses, GroupsService } from '../../core/services/groups.service';
import { LocalHistoryService } from '../../core/services/local-history.service';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { inject, signal } from '@angular/core';
import { ModalService } from '../../core/modal-v2/modal.service';
import { UserBonusesService } from '../../core/services/user/user-bonuses.service';
import { StaticPageService } from '../static-page/static-page.service';
import { LootboxService } from '../../core/services/lootbox/lootbox.service';
import { TimeService } from '../../core/services/time.service';
import { GlobalEventsService } from '../../core/services/global-events.service';
import { WindowService } from '../../core/services/window.service';
import { TranslationService } from '../../core/shared/translation/translation.service';
import { HttpStatus, ResponseHandlerService } from '../../core/services/response-handler.service';
import { CmsApiService } from '../../core/services/api/cms-api.service';
import { ArrayService } from '../../core/services/array.service';
import { GamesService } from '../../core/services/games/games.service';
import { BreakpointsDetectorService } from '../../core/services/breakpoints-detector.service';
import { LanguageService } from '../../core/services/language/language.service';
import { TournamentsService } from '../tournaments/tournaments.service';
import { CopyTextService } from '../../core/services/copy-text.service';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { BonusStage } from '../../core/services/user/data/user-bonuses.data';
import { StoreEntityType } from 'ngx-unificator/store';
import { SeoService } from '../../core/services/seo.service';
import { mapBonusTitle } from '../../core/helpers/utils';


type StringEnum = Record<string, string>;

type ActionRouteConfig = {
  key: string;
  url: string;
  authUrl?: string[];
  nativeUrl?: boolean;
}

type ActionRoutesConfig<E extends StringEnum> = {
  [K in E[keyof E]]: ActionRouteConfig;
};

export enum EventType {
  First = 'first',
  Second = 'second',
  Third = 'third'
}

export const ALREADY_SPINED_SLOT_STR = 'already_spined_slot';

const DISABLE_PROMO_TEMPLATE_SLUG = 'disable-promo-template';

export interface IBasePromo {
  HuntModal: {
    BonusGroupClaimed: string;
    BonusGroupCatch: string;
    Title: string;
    Desc: string;
    Rule: string;
    AboutPromo: string;
    CongratsTitle: string;
    Prize: string;
    FinalDesc: string;
    Code: string;
    DepositRule: string;
    Terms: string;
  };
  WelcomePromoModal: {
    Title: string;
    Desc: string;
    Prize: string;
    Rule: string;
  };
}

export abstract class BasePromo {
  /**
   * Abstract event name for hunt(halloween, easter-eggs ...)
   */
  public abstract eventName: string;

  /**
   * Event type elements
   */
  public eventType = EventType;

  /**
   * Event collected list
   */
  public eventList: unknown[] = null;

  /**
   * Event Loaded
   */
  public eventEnable = signal(false);

  /**
   * Data for hunt (text, userGroup, etc)
   */
  public huntModalData: any = {};

  private _events: GlobalEventsService = inject(GlobalEventsService);
  public array: ArrayService = inject(ArrayService);
  public breakpoint: BreakpointsDetectorService = inject(BreakpointsDetectorService);
  public platform: PlatformService = inject(PlatformService);
  public history: LocalHistoryService = inject(LocalHistoryService);
  public group: GroupsService = inject(GroupsService);
  public window: WindowService = inject(WindowService);
  public bonuses: UserBonusesService = inject(UserBonusesService);
  public user: UserService = inject(UserService);
  public page: StaticPageService = inject(StaticPageService);
  public lootboxService: LootboxService = inject(LootboxService);
  public time: TimeService = inject(TimeService);
  public tournaments: TournamentsService = inject(TournamentsService);
  public device: DeviceDetectorService = inject(DeviceDetectorService);
  public router: Router = inject(Router);
  public translate: TranslationService = inject(TranslationService);
  public responseHandler: ResponseHandlerService = inject(ResponseHandlerService);
  public cmsApi: CmsApiService = inject(CmsApiService);
  public games: GamesService = inject(GamesService);
  public lang: LanguageService = inject(LanguageService);
  public local: LocalHistoryService = inject(LocalHistoryService);
  public modals: ModalService = inject(ModalService);
  public copy: CopyTextService = inject(CopyTextService);
  public cookie: CookieService = inject(CookieService);
  public route: ActivatedRoute = inject(ActivatedRoute);
  public seo: SeoService = inject(SeoService);

  public get isFirstExist(): boolean {
    return this.user.auth && this.eventEnable() && !this.eventList.includes(this.eventType.First);
  }

  public get isSecondExist(): boolean {
    return this.user.auth && this.eventEnable() && !this.eventList.includes(this.eventType.Second);
  }

  public get isThirdExist(): boolean {
    return this.user.auth && this.eventEnable() && !this.eventList.includes(this.eventType.Third);
  }

  public fetchPromoData$(): Observable<any> {
    return this.page
      .item({ slug: this.eventName })
      .pipe(
        filter(page => !!page && !!page[0]),
        map(page => page.map(item => ({
          ...item,
          isDisablePromo: item?.template_slug === DISABLE_PROMO_TEMPLATE_SLUG
        }))),
        switchMap(page => {
          const skipBonusRequest = page[0]?.isDisablePromo;
          return combineLatest([
            of(page),
            ...this.loadNecessaryBonuses$(skipBonusRequest),
          ]);
        })
      );
  }

  public checkIsPromoEnabled(data, obRef: BehaviorSubject<boolean>) {
    if (data?.statusCode) {
      obRef.next(true);
    }
  }

  public loadNecessaryBonuses$(skipBonusRequest: boolean) {
    const shouldLoad = this.user.auth && !skipBonusRequest;
    return [
      shouldLoad ? this.lootboxService.loadUserLootBox() : of([]),
      shouldLoad ? this.bonuses.freeSpinsList() : of([]),
      shouldLoad ? this.bonuses.bonusList() : of([]),
    ];
  }

  public getTournaments$(slug): Observable<any> {
    return this.tournaments.list({ with_game_list: 1 }).pipe(
      filter(tournaments => !!tournaments),
      map(tournaments =>
        tournaments.now.filter(tournament => {
          return tournament.slug.includes(slug);
        }),
      ),
    );
  }

  public isPromoError<T>(promo: T): promo is T & { statusCode: HttpStatus } {
    return (
      typeof promo === 'object' &&
      'statusCode' in promo &&
      (promo.statusCode === HttpStatus.MOVED_PERMANENTLY || promo.statusCode === HttpStatus.NOT_FOUND)
    );
  }

  public getFilteredCashWelcomeBonuses(arrayToCheck: any[]): any {
    return arrayToCheck
      ?.filter(bonus => bonus.stage === BonusStage.HANDLE_BETS && bonus.isWelcome && !this.cookie.check(ALREADY_SPINED_SLOT_STR))
      .sort((a, b) => b.created_at - a.created_at)[0];
  }

  public getFilteredFreespinsWelcomeBonuses(arrayToCheck: any[]): any {
    return arrayToCheck
      ?.filter(bonus => bonus.stage === BonusStage.ISSUED && bonus.isWelcome && !this.cookie.check(ALREADY_SPINED_SLOT_STR))
      .sort((a, b) => b.created_at - a.created_at)[0];
  }

  public getFilteredWelcomeLootbox(arrayToCheck: any[]): any {
    return arrayToCheck?.filter(lootbox => lootbox.isWelcome && lootbox.stage === BonusStage.ISSUED)[0];
  }

  public checkActivatedTodayLootbox(promoLootbox: any, ids: string[]): boolean {
    return (
      promoLootbox?.stage === BonusStage.ACTIVATED && ids.includes(promoLootbox.title) && this.time.isTodayDate(promoLootbox?.created_at)
    );
  }

  public getFilteredPromoLootbox(arrayToCheck: any[], bonusIds: string[], stage: BonusStage): any {
    return arrayToCheck
      ?.filter(lootbox => bonusIds.includes(lootbox?.title) && lootbox?.stage === stage)
      .sort((a, b) => b?.created_at - a?.created_at)[0];
  }

  /**
   * Update current prize store
   * @param fs_count
   * @param cash
   * @param storeRef
   * @private
   */
  public updateCurrentPrize(fs_count: number, cash: string, storeRef: StoreEntityType<any>) {
    storeRef.storeSignal.update(store => {
      return {
        ...store,
        currentPrize: {
          fs_count,
          cash,
        },
      };
    });
    this.cookie.set(ALREADY_SPINED_SLOT_STR, '1');
  }


  public updateParticipateInPromo(lootboxes: any, bonusIds: string[], storeRef: StoreEntityType<any>) {
    const promoIssuedLootbox = storeRef.storeSignal()?.currentBonus?.promoIssuedLootbox;
    const promoActivatedLootbox = this.getFilteredPromoLootbox(lootboxes, bonusIds, BonusStage.ACTIVATED);
    const cash = storeRef.storeSignal()?.currentBonus?.cash;
    const fs = storeRef.storeSignal()?.currentBonus?.fs;
    const welcomeLootbox = storeRef.storeSignal()?.currentBonus?.welcomeLootbox;

    const isUserAlreadyActivatedPromoLootbox = this.checkActivatedTodayLootbox(promoActivatedLootbox, bonusIds);
    const isAllWelcomePackClaimed =
      this.group.isExistGroup(BonusesStatuses.ALL_WELCOME_OFFERS_USED) || this.group.isExistGroup(BonusesStatuses.ALL_SPECIAL_WELCOME_USED);
    const isUserCanParticipateInPromo = Boolean(isAllWelcomePackClaimed && promoIssuedLootbox);
    const isUserCanPartiallyParticipateInPromo = Boolean((cash || fs || welcomeLootbox) && !promoIssuedLootbox);

    storeRef.storeSignal.update(store => {
      return {
        ...store,
        isUserCanParticipateInPromo,
        isUserCanPartiallyParticipateInPromo,
        isAllWelcomePackClaimed,
        isUserAlreadyActivatedPromoLootbox,
      };
    });
  }

  public updateCurrentBonus(lootboxes: any, freespins: any, bonuses: any, bonusIds: string[], storeRef: StoreEntityType<any>) {
    const cash = this.getFilteredCashWelcomeBonuses(bonuses);
    const fs = this.getFilteredFreespinsWelcomeBonuses(freespins);
    const welcomeLootbox = this.getFilteredWelcomeLootbox(lootboxes);
    const promoIssuedLootbox = this.getFilteredPromoLootbox(lootboxes, bonusIds, BonusStage.ISSUED);

    storeRef.storeSignal.update(store => {
      return {
        ...store,
        currentBonus:
          fs || cash || (welcomeLootbox && cash) || fs || promoIssuedLootbox
            ? {
                cash,
                fs,
                welcomeLootbox,
                promoIssuedLootbox,
              }
            : null,
      };
    });
  }

  public async onAction<E extends StringEnum>(
    actionEnum: E,
    action: E[keyof E],
    configMap: ActionRoutesConfig<E>,
    bonusCode?: string,
    successUrl: string = this.window.nativeWindow.location.href + '?deposit_success=true',
  ) {
    const routeConfig = configMap[action];
    const { key, url, authUrl } = routeConfig;

    if (action === actionEnum.GO_TO_DEPOSIT) {
      await this._handleActionRoute(key, url, authUrl, { bonusCode }, { queryParams: { successUrl, bonusCode } });
    } else if (action === actionEnum.HUNT_DEPOSIT) {
      const promoUrl = `${this.window.nativeWindow.location.origin}/promo/${this.eventName}?deposit_success=true`;
      await this._handleActionRoute(key, url, authUrl, { bonusCode }, { queryParams: { successUrl: promoUrl, bonusCode } });
    } else if (configMap[action]?.nativeUrl) {
      this.window.nativeWindow.open(configMap[action].url, '_blank');
    } else {
      await this._handleActionRoute(key, url, authUrl);
    }
  }

  /**
   * Activate bonus by type of bonus
   */
  public onActivateBonus$(storeRef: StoreEntityType<any>): Observable<any> {
    const store = storeRef.storeSignal();
    const { promoIssuedLootbox, welcomeLootbox, fs, cash } = store.currentBonus || {};

    const cashPrize = cash ? `${cash.amount} ${cash.currency}` : undefined;

    if (store.isUserCanPartiallyParticipateInPromo) {
      if (welcomeLootbox) {
        return this.lootboxService.activateLootBox(welcomeLootbox.id).pipe(
          switchMap(response => {
            const fs_count = this.bonuses.resolveBonusAttributes(response?.bonuses[0].attributes)?.freespins_count;
            this.updateCurrentPrize(fs_count, cashPrize ?? undefined, storeRef);
            const currentFsBonus = mapBonusTitle(response?.bonuses[0]?.title);
            return this.bonuses.freeSpinsList().pipe(
              switchMap(list => {
                const foundFs = list.find(e => e.title === currentFsBonus);
                return this.bonuses.activateFreeSpins(foundFs?.id);
              }),
            );
          }),
        );
      } else if (fs) {
        return this.bonuses.activateFreeSpins(fs?.id).pipe(
          tap(() => {
            this.updateCurrentPrize(fs?.freespins_total, cashPrize ?? undefined, storeRef);
          }),
        );
      }
    }

    if (promoIssuedLootbox) {
      return this.lootboxService.activateLootBox(promoIssuedLootbox?.id).pipe(
        switchMap(response => {
          const fs_count = this.bonuses.resolveBonusAttributes(response?.bonuses[0].attributes)?.freespins_count;
          this.updateCurrentPrize(fs_count, '', storeRef);
          const currentFsBonus = mapBonusTitle(response?.bonuses[0]?.title);
          storeRef.storeSignal.update((e) => {
            return {
              ...e,
              showTryTomorrowOnDestroy: true,
            }
          });
          return this.bonuses.freeSpinsList().pipe(
            switchMap(list => {
              const foundFs = list.find(e => e.title === currentFsBonus);
              return this.bonuses.activateFreeSpins(foundFs?.id);
            }),
          );
        }),
      );
    }

    if (cash) {
      return of(null).pipe(
        tap(() => {
          this.updateCurrentPrize(null, cashPrize ?? undefined, storeRef);
        })
      );
    }
  }


  private async _handleActionRoute(
    key: string,
    url: string,
    authUrl: string[],
    urlData?: Record<string, any>,
    extras?: NavigationExtras,
  ): Promise<void> {
    if (!key) {
      await this.router.navigateByUrl(url);
      return;
    }

    if (!this.user.auth) {
      this.local.addUrl(key, url, urlData);
      const isRegistered = this.user.isUserAlreadyRegistered();
      const urlReg = this.router.url + (isRegistered ? '?modal=login' : '?modal=registration');
      await this.router.navigateByUrl(urlReg);
      return;
    } else {
      await this.router.navigate(authUrl, extras);
    }
  }

  public addEventToCollection(type: EventType) {
    if (this.platform.isBrowser) {
      this.eventList.push(type);
      this.cookie.set(this.eventName, JSON.stringify(this.eventList), 999, '/');
      if (this.eventList.length === 3) {
        this.group.addToGroup(this.huntModalData?.HuntModal?.BonusGroupCatch)?.subscribe();
      }
      this.openHuntModal(type);
    }
  }

  public initHuntEvent(HuntModal: any) {
    this.huntModalData = HuntModal;
    if (!this.group.isExistGroup(this.huntModalData?.HuntModal?.BonusGroupClaimed)) {
      if (this.group.isExistGroup(this.huntModalData?.HuntModal?.BonusGroupCatch)) {
        this.eventList = [this.eventType.First, this.eventType.Second, this.eventType.Third];
      } else {
        this.eventList = this.cookie.check(this.eventName) ? JSON.parse(this.cookie.get(this.eventName)) : [];
      }
      this.eventEnable.set(true);
    } else {
      this.eventEnable.set(false);
    }
  }

  public initWelcomePromoModal() {
    if (this.user.auth && this.platform.isBrowser) {
      this._checkShouldOpenWelcomeModal$().subscribe();
    }
  }

  private _checkShouldOpenWelcomeModal$(): Observable<boolean> {
    const routesIgnore = ['403', '404', 'register', 'login', 'deposit', 'cashout', 'games', 'promo', 'play', 'bonuses', 'history'];
    return this._events.routerNavigationEnd$.pipe(
      takeWhile(() => !this.cookie.check('welcome-modal-promo'), true),
      distinctUntilChanged(),
      delay(120000),
      filter(() => {
        const hasWelcomeModalCookie = this.cookie.check('welcome-modal-promo');
        const shouldOpenModal = this.user.auth && !hasWelcomeModalCookie;
        return shouldOpenModal && !routesIgnore.some(route => this.router.url.includes(route));
      }),
      switchMap(() => this.checkActivatedBonuses()),
    );
  }

  public abstract openHuntModal?(type: EventType): void;

  public abstract checkActivatedBonuses?(): Observable<any> | Promise<any>;
}
